import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Refresh, Visibility } from "@mui/icons-material";
import PageHeaderLayout from "../../../components/Layout/PageHeaderLayout";
import Loading from "../../../components/Loader/Loading";
import CustomPagination from "../../../components/pagination/CustomPagination";
import { commonRequest } from "../../../services/ApiCall";
import { Helpers } from "../../../services/Helpers";

const actionLabels = {
  VIEW_PAN: "Viewed PAN",
  VIEW_AADHAAR_FRONT: "Viewed Aadhaar front",
  VIEW_AADHAAR_BACK: "Viewed Aadhaar back",
  UPDATE_KYC: "Updated KYC",
};

const changedFieldLabels = {
  panNumber: "PAN number",
  panImage: "PAN image",
  aadharNumber: "Aadhaar number",
  aadharFrontImage: "Aadhaar front image",
  aadharRearImage: "Aadhaar back image",
  isPanVerified: "PAN verification",
  isAadhharVerified: "Aadhaar verification",
  isKycVerified: "KYC verification",
};

const formatDate = (value) => value ? new Intl.DateTimeFormat("en-IN", {
  dateStyle: "medium",
  timeStyle: "medium",
}).format(new Date(value)) : "—";

const documentLabel = (row) => {
  if (row.documentType === "PAN") return "PAN";
  if (row.documentType?.startsWith("AADHAAR")) return "Aadhaar";
  const fields = row.changedFields || [];
  const documents = [];
  if (fields.some((field) => field.toLowerCase().includes("pan"))) documents.push("PAN");
  if (fields.some((field) => field.toLowerCase().includes("aadhar"))) documents.push("Aadhaar");
  return documents.join(" & ") || "KYC";
};

export default function KycAuditLogs() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "kyc-audit-logs" });
  }, [dispatch]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      page: String(paginationModel.page + 1),
      limit: String(paginationModel.pageSize),
    });
    const response = await commonRequest(
      "GET",
      `${Helpers.URL()}/api/admin/user/kyc-logs?${params}`,
      {},
      Helpers.token()
    );
    if (response?.success === 1) {
      setRows(response.data.logs || []);
      setRowCount(response.data.totalItems || 0);
    } else {
      setRows([]);
      setRowCount(0);
      setError(response?.message || "Unable to load KYC audit logs");
    }
    setLoading(false);
  }, [paginationModel]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const columns = useMemo(() => [
    {
      field: "createdAt",
      headerName: "Date & time",
      width: 190,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "action",
      headerName: "Event",
      width: 190,
      renderCell: ({ value }) => <Chip size="small" label={actionLabels[value] || value} color={value === "UPDATE_KYC" ? "primary" : "info"} />,
    },
    {
      field: "document",
      headerName: "Document",
      width: 130,
      valueGetter: ({ row }) => documentLabel(row),
    },
    {
      field: "expertName",
      headerName: "Expert",
      minWidth: 190,
      flex: 1,
      valueGetter: ({ row }) => row.expertName ? `${row.expertName} (#${row.expertId})` : `Expert #${row.expertId}`,
    },
    {
      field: "admin",
      headerName: "Admin",
      minWidth: 220,
      flex: 1,
      valueGetter: ({ row }) => row.adminEmail || `Admin #${row.adminId}`,
    },
    {
      field: "changedFields",
      headerName: "Changed details",
      minWidth: 280,
      flex: 1,
      valueFormatter: ({ value }) => value?.map((field) => changedFieldLabels[field] || field).join(", ") || "—",
    },
    { field: "ipAddress", headerName: "IP address", width: 150 },
    {
      field: "details",
      headerName: "Details",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View KYC audit details">
          <Button size="small" aria-label="View KYC audit details" onClick={() => setSelected(row)}>
            <Visibility fontSize="small" />
          </Button>
        </Tooltip>
      ),
    },
  ], []);

  return (
    <>
      <PageHeaderLayout headerTitle={`KYC Audit Logs (${rowCount})`}>
        <Button size="small" startIcon={<Refresh />} onClick={loadLogs}>Refresh</Button>
      </PageHeaderLayout>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Card>
        <Box sx={{ overflow: "auto" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            disableColumnMenu
            disableDensitySelector
            disableRowSelectionOnClick
            autoHeight
            slots={{ loadingOverlay: Loading, pagination: CustomPagination }}
            sx={{
              minWidth: 1100,
              "& .MuiDataGrid-row .MuiDataGrid-cell": { py: 1, fontSize: ".7rem" },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
            }}
          />
        </Box>
      </Card>
      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>KYC audit details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Grid container spacing={1}>
                {[
                  ["Date & time", formatDate(selected.createdAt)],
                  ["Event", actionLabels[selected.action] || selected.action],
                  ["Document", documentLabel(selected)],
                  ["Expert name", selected.expertName],
                  ["Expert ID", selected.expertId],
                  ["Admin", selected.adminEmail || `Admin #${selected.adminId}`],
                  ["Admin role ID", selected.adminRoleId],
                  ["IP address", selected.ipAddress],
                  ["Request ID", selected.requestId],
                  ["User agent", selected.userAgent],
                  ["Changed details", selected.changedFields?.map((field) => changedFieldLabels[field] || field).join(", ")],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{value || "—"}</Typography>
                  </Grid>
                ))}
              </Grid>
              {selected.before && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Before</Typography>
                  <Box component="pre" sx={{ bgcolor: "grey.100", borderRadius: 1, fontSize: ".75rem", m: 0, overflow: "auto", p: 1.5 }}>
                    {JSON.stringify(selected.before, null, 2)}
                  </Box>
                </Box>
              )}
              {selected.after && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>After</Typography>
                  <Box component="pre" sx={{ bgcolor: "grey.100", borderRadius: 1, fontSize: ".75rem", m: 0, overflow: "auto", p: 1.5 }}>
                    {JSON.stringify(selected.after, null, 2)}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
}
